import { useAuth } from "@mindlercare/auth-ui";
import { RouterOutput, ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useMutation } = ReactQueryV5;
import { Icon, Render } from "@mindlercare/ui-web";
import TextField from "@mui/material/TextField";
import { ColumnDef } from "@tanstack/react-table";
import { useMindlerApi, useMindlerApiVanillaClient } from "api/apiv2";
import { Table } from "components/Table";
import CONSTANTS from "core/constants";
import { FormEvent, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";

export type PatientSearchResult =
  RouterOutput["patients"]["searchPatient"][number];
type GetPatientByUserIdResult = RouterOutput["patients"]["getByUserId"];

export const PatientSearch = ({
  psychologistId,
  handleGetPatientData,
}: {
  psychologistId: number;
  handleGetPatientData: (patient: GetPatientByUserIdResult) => Promise<void>;
}) => {
  const { market } = useAuth();
  const [patients, setPatients] = useState<PatientSearchResult[]>([]);
  const [searchString, setSearchString] = useState("");

  const { t } = useTranslation();
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const api = useMindlerApi();
  const vanillaClient = useMindlerApiVanillaClient();
  const { mutate } = useMutation(
    api.patients.searchPatient.mutationOptions({
      onSuccess(data) {
        setPatients(data);
      },
    }),
  );

  const searchPatient = (value: string) => {
    setSearchString(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      mutate({
        searchString: value,
        selectedPsychologistId: psychologistId,
      });
    }, CONSTANTS.SEARCH_TIMEOUT);
  };

  const searchPatientDirect = async (e: FormEvent) => {
    e.preventDefault();
    if (!searchString) return;

    mutate({
      searchString,
      selectedPsychologistId: psychologistId,
    });
  };

  const tablePatients = useMemo(
    () =>
      patients.map((p) => ({
        firstName: p.firstName,
        lastName: p.lastName,
        nationalIdentity: p.nationalIdentity,
        userId: p.userId,
        id: `${p.userId}`,
      })),
    [patients],
  );

  const patientSearchColumns = useMemo<
    ColumnDef<(typeof tablePatients)[number]>[]
  >(
    () => [
      {
        header: "FIRSTNAME",
        accessorKey: "firstName",
        cell: (info) => info.getValue(),
        meta: {
          sensitive: true,
        },
      },
      {
        header: "LASTNAME",
        accessorKey: "lastName",
        cell: (info) => info.getValue(),
        meta: {
          sensitive: true,
        },
      },
      {
        header: "USER_ID",
        accessorKey: "userId",
        cell: (info) => info.getValue(),
        meta: {
          sensitive: true,
        },
      },
      {
        header: "NATIONAL_IDENTITY",
        accessorKey: "nationalIdentity",
        cell: (info) => info.getValue(),
        meta: {
          sensitive: true,
        },
      },
    ],
    [],
  );

  type TablePatient = (typeof tablePatients)[number];
  const clickHandler = async (row: TablePatient) => {
    setSearchString("");
    vanillaClient.patients.getByUserId
      .query({
        patientUserId: row.userId,
      })
      .then((result) => {
        handleGetPatientData(result);
      });
  };
  return (
    <PatientSearchWrapper>
      <PatientSearchInputWrapper>
        <form onSubmit={searchPatientDirect}>
          <SearchIconWrapper>
            <Icon.Search />
          </SearchIconWrapper>
          <PatientSearchTextField
            variant="standard"
            autoComplete={"off"}
            data-testid="cy-patient-search"
            fullWidth={true}
            value={searchString}
            label={t("SEARCH_BY_PATIENT_NAME")}
            onChange={(e) => searchPatient(e.target.value)}
          />
        </form>
      </PatientSearchInputWrapper>
      <Render if={patients.length > 0 && !!searchString}>
        <Table
          columns={patientSearchColumns}
          styles={{
            rowPadding: "left",
            noMargin: true,
            borderShadow: true,
            isDropdown: true,
          }}
          data={tablePatients}
          onRowClick={clickHandler}
          columnVisibility={{
            nationalIdentity: market === "SE",
          }}
        />
      </Render>
    </PatientSearchWrapper>
  );
};

const PatientSearchWrapper = styled.div`
  position: relative;
  margin-top: -8px;
  padding-bottom: 24px;
`;

const PatientSearchInputWrapper = styled.div`
  position: relative;
`;

const SearchIconWrapper = styled.span`
  position: absolute;
  bottom: 5px;
`;

export const PatientSearchTextField = styled(TextField)`
  && {
    .MuiInputLabel-root {
      padding-left: 34px;
      top: 2px;
      color: ${({ theme }) => theme.COLORS.GRAY_MEDIUM};
      font-size: ${({ theme }) => theme.FONT.SIZE.STANDARD};
      font-weight: ${({ theme }) => theme.FONT.WEIGHT.REGULAR};
    }

    .MuiInputLabel-shrink {
      display: none;
    }

    .MuiInputBase-root {
      padding-left: 34px;
      font-family: ${({ theme }) => theme.FONT.FAMILY.BODY};
      font-weight: ${({ theme }) => theme.FONT.WEIGHT.REGULAR};
      color: ${({ theme }) => theme.COLORS.GRAY_BLACK_OPACITY};

      input {
        padding: 4px 0;
        font-size: ${({ theme }) => theme.FONT.SIZE.STANDARD};
      }

      &:before {
        border-bottom: 1px solid ${({ theme }) => theme.COLORS.GRAY_MEDIUM};
      }

      &:after {
        border-bottom: 1px solid ${({ theme }) => theme.COLORS.GRAY_MEDIUM};
      }

      &:hover:not(.Mui-focused):not(.Mui-disabled):not(.Mui-error) {
        &:before,
        &:after {
          border-bottom: 1px solid ${({ theme }) => theme.COLORS.GRAY_MEDIUM};
        }
      }

      &.Mui-focused {
        color: ${({ theme }) => theme.COLORS.GRAY_BLACK};

        &:before,
        &:after {
          border-bottom-color: ${({ theme }) => theme.COLORS.GRAY_BLACK};
        }
      }
    }
  }
`;
